/**
 * IntegrityVerifier — HMAC-SHA256 signing and verification for block manifests.
 *
 * Signs a set of block files (filename → content) into a BlockManifestSig,
 * and verifies that file contents have not been tampered with by recomputing
 * SHA-256 hashes and validating the HMAC-SHA256 signature.
 *
 * Uses Web Crypto API (globalThis.crypto.subtle) — works in browsers, Node 20+,
 * Cloudflare Workers, Deno, and React Native.
 */

export interface BlockManifestSig {
  version: number // always 1
  algorithm: string // "HMAC-SHA256"
  files: Record<string, string> // filename -> SHA-256 hex hash
  signature: string // HMAC-SHA256 hex of sorted file hashes
}

/** Convert ArrayBuffer to lowercase hex string */
function bufToHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
}

export class IntegrityVerifier {
  /**
   * Compute the SHA-256 hash of a string, returned as a 64-char lowercase hex string.
   */
  async hashContent(content: string): Promise<string> {
    const encoded = new TextEncoder().encode(content)
    const buf = await globalThis.crypto.subtle.digest("SHA-256", encoded)
    return bufToHex(buf)
  }

  /**
   * Create a signed manifest for a set of files.
   *
   * 1. Hash each file's content with SHA-256.
   * 2. Sort filenames alphabetically.
   * 3. Build payload: "file1:hash1|file2:hash2|..."
   * 4. HMAC-SHA256 sign the payload with the provided key.
   * 5. Return the manifest object.
   */
  async createManifest(
    files: Record<string, string>,
    hmacKey: string,
  ): Promise<BlockManifestSig> {
    const fileHashes: Record<string, string> = {}

    for (const [filename, content] of Object.entries(files)) {
      fileHashes[filename] = await this.hashContent(content)
    }

    const signature = await this.computeSignature(fileHashes, hmacKey)

    return {
      version: 1,
      algorithm: "HMAC-SHA256",
      files: fileHashes,
      signature,
    }
  }

  /**
   * Verify a manifest against actual file contents and a signing key.
   *
   * 1. Check all manifest files are present and hashes match.
   * 2. Recompute the HMAC signature.
   * 3. Compare with the manifest's stored signature.
   */
  async verifyManifest(
    files: Record<string, string>,
    manifest: BlockManifestSig,
    hmacKey: string,
  ): Promise<boolean> {
    const manifestFilenames = Object.keys(manifest.files)

    // Every file in the manifest must be present in the provided files
    for (const filename of manifestFilenames) {
      if (!(filename in files)) {
        return false
      }
    }

    // Recompute hashes and compare with manifest
    const recomputedHashes: Record<string, string> = {}
    for (const filename of manifestFilenames) {
      const hash = await this.hashContent(files[filename])
      if (hash !== manifest.files[filename]) {
        return false
      }
      recomputedHashes[filename] = hash
    }

    // Recompute HMAC signature and compare
    const expectedSignature = await this.computeSignature(recomputedHashes, hmacKey)
    return expectedSignature === manifest.signature
  }

  /**
   * Build the sorted payload string and compute its HMAC-SHA256 signature.
   */
  private async computeSignature(
    fileHashes: Record<string, string>,
    hmacKey: string,
  ): Promise<string> {
    const sortedFilenames = Object.keys(fileHashes).sort()
    const payload = sortedFilenames
      .map((f) => `${f}:${fileHashes[f]}`)
      .join("|")

    const keyData = new TextEncoder().encode(hmacKey)
    const key = await globalThis.crypto.subtle.importKey(
      "raw",
      keyData,
      { name: "HMAC", hash: "SHA-256" },
      false,
      ["sign"],
    )
    const sig = await globalThis.crypto.subtle.sign(
      "HMAC",
      key,
      new TextEncoder().encode(payload),
    )
    return bufToHex(sig)
  }
}
